// v2.19.33 — Page owner Digests Mensuels.
// Liste fiches du mois avec stats + bouton WhatsApp per row + bouton bulk + badge "envoyé".
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function _currentMonth() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function _availableMonths() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const dd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    const ym = `${dd.getUTCFullYear()}-${String(dd.getUTCMonth() + 1).padStart(2, "0")}`;
    out.push(ym);
  }
  return out;
}

export default function OpsDigestsPage() {
  const [month, setMonth] = useState(_currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ops-b2b/digests/monthly?month=${month}`, { headers: { ..._authHeaders() } });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) setData(d);
      else if (r.status === 401 || r.status === 403) toast.error("Accès refusé : connecte-toi en owner");
      else toast.error(d.error || `Erreur ${r.status}`);
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const onSendOne = async (digest) => {
    window.open(digest.whatsapp.wa_url, "_blank", "noopener,noreferrer");
    try {
      await fetch(`${API}/api/ops-b2b/digests/mark-clicked`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({ fiche_id: digest.fiche_id, month }),
      });
      load();
    } catch (_) {}
  };

  const onSendBulk = async () => {
    if (!data?.digests?.length) return;
    if (!window.confirm(`Envoyer le digest WhatsApp à ${data.digests.length} fiches ?\nUn onglet par fiche s'ouvrira (délai 800ms).`)) return;
    for (let i = 0; i < data.digests.length; i++) {
      const dg = data.digests[i];
      await new Promise((r) => setTimeout(r, 800));
      window.open(dg.whatsapp.wa_url, "_blank", "noopener,noreferrer");
      try {
        await fetch(`${API}/api/ops-b2b/digests/mark-clicked`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ..._authHeaders() },
          body: JSON.stringify({ fiche_id: dg.fiche_id, month }),
        });
      } catch (_) {}
    }
    toast.success(`✅ ${data.digests.length} digests dispatchés`);
    load();
  };

  return (
    <div data-testid="ops-digests-page" style={pageStyle}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "#FBBF24" }}>📨 Digests Mensuels</h1>
        <a href="/mon-compte" style={{ color: "#94A3B8", fontSize: 13, textDecoration: "underline" }}>← Mon Compte</a>
      </header>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "rgba(232,236,247,0.7)" }}>
        Envoie un récap WhatsApp aux owners de tes fiches B2B pour leur montrer leur visibilité AYA.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="digests-month-select" style={selectStyle}>
          {_availableMonths().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {data?.month_label && <span style={{ color: "#FBBF24", fontWeight: 700, fontSize: 14 }}>{data.month_label}</span>}
        {data?.digests?.length > 0 && (
          <button onClick={onSendBulk} data-testid="digests-bulk-btn" style={bulkBtnStyle}>📤 Envoyer à TOUS ({data.digests.length})</button>
        )}
        <button onClick={load} disabled={loading} data-testid="digests-refresh" style={miniBtnStyle}>{loading ? "…" : "↻"}</button>
      </div>

      {loading && <div style={{ color: "#94A3B8", textAlign: "center", padding: 24 }}>Chargement…</div>}
      {!loading && (!data || data.digests.length === 0) && (
        <div data-testid="digests-empty" style={emptyStyle}>
          Aucune fiche n'a reçu d'impressions ce mois. Reviens après quelques requêtes utilisateurs.
        </div>
      )}
      {!loading && data?.digests?.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.digests.map((d) => {
            const meta = d.sent;
            const sign = d.stats.delta_pct === null
              ? null
              : (d.stats.delta_pct >= 0 ? `+${d.stats.delta_pct}%` : `${d.stats.delta_pct}%`);
            const arrow = d.stats.delta_sign === 'up' ? '📈' : (d.stats.delta_sign === 'down' ? '📉' : '🆕');
            return (
              <div key={d.fiche_id} data-testid={`digest-row-${d.fiche_id}`} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 15, color: "#fff" }}>{d.fiche_name}</strong>
                    <span style={badgeStyle}>{d.fiche_collection}</span>
                    {meta?.clicked_at && (
                      <span data-testid={`digest-sent-${d.fiche_id}`} style={sentBadgeStyle}>
                        ✅ Envoyé le {new Date(meta.clicked_at).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>
                    {arrow} <strong style={{ color: "#FBBF24" }}>{d.stats.impressions_current_month}</strong> citations
                    {sign && <> ({sign} vs {data.previous_month_label})</>}
                    {d.stats.top_communes?.length > 0 && <> · 📍 {d.stats.top_communes.join(", ")}</>}
                    {d.stats.top_keywords?.length > 0 && <> · 💬 {d.stats.top_keywords.join(", ")}</>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onSendOne(d)}
                  data-testid={`digest-send-${d.fiche_id}`}
                  style={sendBtnStyle(!!meta?.clicked_at)}
                  title={meta?.clicked_at ? "Renvoyer le digest" : "Envoyer le digest WhatsApp"}
                >
                  {meta?.clicked_at ? "🔄 Renvoyer" : "📤 Envoyer"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const pageStyle = { minHeight: "100vh", padding: "24px 18px", background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", fontFamily: "system-ui, -apple-system, sans-serif", maxWidth: 1000, margin: "0 auto" };
const selectStyle = { padding: "8px 12px", background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit", cursor: "pointer" };
const bulkBtnStyle = { padding: "8px 14px", background: "#25D366", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, boxShadow: "0 3px 10px rgba(37,211,102,0.3)", fontFamily: "inherit" };
const miniBtnStyle = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", width: 32, height: 32, borderRadius: 6, cursor: "pointer", fontSize: 14 };
const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" };
const badgeStyle = { fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(59,130,246,0.18)", color: "#93C5FD", border: "1px solid rgba(59,130,246,0.35)", fontWeight: 600 };
const sentBadgeStyle = { fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(34,197,94,0.18)", color: "#86EFAC", border: "1px solid rgba(34,197,94,0.4)", fontWeight: 700 };
const sendBtnStyle = (alreadySent) => ({
  padding: "9px 14px",
  background: alreadySent ? "rgba(148,163,184,0.18)" : "#25D366",
  color: alreadySent ? "#94A3B8" : "#fff",
  border: alreadySent ? "1px solid rgba(148,163,184,0.4)" : "none",
  borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0,
});
const emptyStyle = { padding: "30px 10px", textAlign: "center", color: "#94A3B8", fontSize: 13, background: "rgba(255,255,255,0.03)", borderRadius: 10 };
